import { useState } from 'react'

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { Button, CircularProgress } from '@mui/material'
import { format } from 'date-fns'
import { toast } from 'react-toastify'

import { ISO_8601_DATE_FORMAT } from 'constants/DateFormats'
import {
  useGetSupplierActivityRecencyExportRowsLazyQuery,
  VehicleTypeFilter,
} from 'generated/graphql'
import { downloadCsv, toCsv } from 'helpers/csv.helpers'
import { formatActivityDate, NEVER } from 'helpers/supplierActivityRecency.helpers'

import { VEHICLE_TYPES } from '../constants/vehicleTypes'

interface Props {
  vehicleType: VehicleTypeFilter
  windowDays: number
}

export const SupplierActivityRecencyExportButton = ({ vehicleType, windowDays }: Props) => {
  const [isExporting, setIsExporting] = useState(false)
  const [fetchExportRows] = useGetSupplierActivityRecencyExportRowsLazyQuery()

  const handleExportCsv = async () => {
    setIsExporting(true)

    try {
      const { data } = await fetchExportRows({ variables: { input: { vehicleType, windowDays } } })
      const rows = data?.supplierActivityRecencyExportRows ?? []

      if (rows.length === 0) {
        toast.info('There is nothing to export for the selected filters.')
        return
      }

      // Every row carries the same vehicle type - it is the filter the rows were read under - but
      // it belongs in the file so a saved export still says what it covers.
      const vehicleLabel =
        VEHICLE_TYPES.find((option) => option.value === vehicleType)?.label ?? vehicleType

      const csv = toCsv(
        [
          'Supplier ID',
          'Supplier name',
          'Vehicle type',
          'Days since last bid',
          'Last bid date',
          'Days since last completed job',
          'Last completed job date',
        ],
        rows.map((row) => [
          row.supplierId,
          row.supplierName,
          vehicleLabel,
          row.daysSinceLastBid ?? NEVER,
          formatActivityDate(row.lastBidAt),
          row.daysSinceLastCompletedJob ?? NEVER,
          formatActivityDate(row.lastCompletedJobAt),
        ]),
      )

      // Derived from the enum rather than a lookup, so a new filter cannot leave two different
      // exports sharing one file name — the browser would silently suffix the second with (1).
      const vehicleSlug = vehicleType.toLowerCase().replace(/_/g, '-')
      const date = format(new Date(), ISO_8601_DATE_FORMAT)

      downloadCsv(csv, `supplier-activity-recency-${vehicleSlug}-${date}.csv`)
    } catch {
      toast.error('The export could not be generated. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant='outlined'
      startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
      disabled={isExporting}
      onClick={handleExportCsv}
      sx={{ whiteSpace: 'nowrap', flexShrink: 0, height: 40 }}>
      Export CSV
    </Button>
  )
}
